// ============================================================================
// SECTION 1: IMPORTS & SCHEMAS
// ============================================================================
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import StudentOnboarding from './StudentOnboarding'; 
import BookingCalendar from './BookingCalendar';

import { DYNAMIC_VEHICLE_FRAMEWORKS, MASTER_READINESS_CHECKLIST, scoringGuide } from './constants';
import * as Styles from './styles';
import LearnerHelpModal from './LearnerHelpModal';
import schoolLogo from '../school-brand.jpg';

// ============================================================================
// SECTION 2: COMPONENT INITIALIZATION & STATE
// ============================================================================
function LearnerDashMobile() {
  const { instructorId } = useParams();
  const navigate = useNavigate();

  // -- General Data State --
  const [instructor, setInstructor] = useState(null);
  const [template, setTemplate] = useState([]);
  const [existingBookings, setExistingBookings] = useState([]);
  const [currentWeekStart, setCurrentWeekStart] = useState(new Date());
  const [studentHistoricalLogs, setStudentHistoricalLogs] = useState([]);
  const [studentProfile, setStudentProfile] = useState(null);

  // -- UI & Interaction State --
  const [mobileTab, setMobileTab] = useState('menu'); // 🔑 Starts on menu view ('menu', 'book', 'bulk', 'progress', 'chat')
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [selectedAuditLogId, setSelectedAuditLogId] = useState('');
  const [isBooking, setIsBooking] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);

  // -- Booking / Cart State --
  const [stagedBookings, setStagedBookings] = useState([]);
  const [recurringDay, setRecurringDay] = useState('1'); 
  const [recurringStartTime, setRecurringStartTime] = useState('10:00');
  const [recurringEndTime, setRecurringEndTime] = useState('14:00');
  const [recurringWeeks, setRecurringWeeks] = useState('4');

  // -- Resolved IDs --
  const sessionLearnerId = localStorage.getItem('learnerId') || localStorage.getItem('userId');
  const sessionEmail = localStorage.getItem('userEmail') || localStorage.getItem('learnerEmail');
  const learnerName = localStorage.getItem('userName') || "Learner Driver";
  const targetInstructorId = instructorId || localStorage.getItem('instructorId') || 5;
  const timeLabels = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}:00`);
  const [learnerId, setLearnerId] = useState(sessionLearnerId || 1);

  // -- Messaging State --
  const [messagesList, setMessagesList] = useState([]);
  const [newMessageText, setNewMessageText] = useState('');

  const fetchMessages = useCallback(async () => {
    if (!targetInstructorId || !learnerId) return;
    try {
      const res = await fetch(`/api/messages/${targetInstructorId}/${learnerId}`);
      if (res.ok) {
        const data = await res.json();
        const sorted = Array.isArray(data) ? data.sort((a, b) => new Date(a.created_at) - new Date(b.created_at)) : [];
        setMessagesList(sorted);
      }
    } catch (err) {
      console.error("Error fetching messages:", err);
    }
  }, [targetInstructorId, learnerId]);

  useEffect(() => {
    if (targetInstructorId && learnerId) {
      fetchMessages();
      const interval = setInterval(fetchMessages, 6000);
      return () => clearInterval(interval);
    }
  }, [targetInstructorId, learnerId, fetchMessages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessageText.trim()) return;

    try {
      const res = await fetch('/api/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          instructor_id: parseInt(targetInstructorId, 10),
          learner_id: parseInt(learnerId, 10),
          sender_role: 'learner',
          message_text: newMessageText.trim()
        })
      });

      if (res.ok) {
        setNewMessageText('');
        fetchMessages();
      }
    } catch (err) {
      console.error("Error sending message:", err);
    }
  };

  // ============================================================================
  // SECTION 3: ROBUST DATA FETCHING
  // ============================================================================
  const fetchScheduleData = useCallback(async () => {
    if (!targetInstructorId) return;
    try {
      const iRes = await fetch(`/api/instructors/${targetInstructorId}`);
      if (iRes.ok) {
        const iData = await iRes.json();
        setInstructor(iData);
        if (iData.calendar_template) {
          const parsed = typeof iData.calendar_template === 'string' ? JSON.parse(iData.calendar_template) : iData.calendar_template;
          setTemplate(Array.isArray(parsed) ? parsed : []);
        } else {
          setTemplate([]);
        }
      }

      const bRes = await fetch(`/api/bookings?instructorId=${targetInstructorId}`);
      if (bRes.ok) {
        const bData = await bRes.json();
        setExistingBookings(Array.isArray(bData) ? bData : []);
      }

      const learnersRes = await fetch(`/api/learners?instructorId=${targetInstructorId}`);
      if (learnersRes.ok) {
        const learnersData = await learnersRes.json();
        let currentMe = learnersData.find(l => 
          (sessionLearnerId && parseInt(l.id, 10) === parseInt(sessionLearnerId, 10)) ||
          (sessionEmail && l.email && l.email.toLowerCase() === sessionEmail.toLowerCase())
        );

        if (!currentMe && learnersData.length > 0) currentMe = learnersData[0];

        if (currentMe) {
          setLearnerId(currentMe.id);
          setStudentProfile({
            ...currentMe,
            training_category: currentMe.training_category || "Car (Manual)", 
            skills: typeof currentMe.skills === 'string' ? JSON.parse(currentMe.skills) : (currentMe.skills || {}),
            test_readiness_checklist: typeof currentMe.test_readiness_checklist === 'string' ? JSON.parse(currentMe.test_readiness_checklist) : (currentMe.test_readiness_checklist || [])
          });

          const logsRes = await fetch(`/api/lesson-logs/${currentMe.id}`);
          if (logsRes.ok) {
            const logsData = await logsRes.json();
            const sortedLogs = Array.isArray(logsData) ? logsData.sort((a, b) => new Date(b.lesson_date) - new Date(a.lesson_date)) : [];
            setStudentHistoricalLogs(sortedLogs);
            if (sortedLogs.length > 0 && !selectedAuditLogId) {
              setSelectedAuditLogId(String(sortedLogs[0].id));
            }
          }
        }
      }
    } catch (err) {
      console.error("Dependency Sync Matrix Crash:", err);
    }
  }, [targetInstructorId, sessionLearnerId, sessionEmail, selectedAuditLogId]);

  useEffect(() => {
    fetchScheduleData();
  }, [fetchScheduleData, currentWeekStart]);

  useEffect(() => {
    if (localStorage.getItem('forceOnboarding') === 'true') {
      setShowSettings(true);
    }
  }, []);

  // ============================================================================
  // SECTION 4: PROGRESS CALCULATIONS
  // ============================================================================
  const calculateStudentProgress = () => {
    const categoryString = studentProfile?.training_category || "Car (Manual)";
    const curriculumFramework = DYNAMIC_VEHICLE_FRAMEWORKS[categoryString];
    if (!curriculumFramework) return 0;

    const totalSkillsInCurriculum = Object.values(curriculumFramework).flat().length;
    const studentSkillsMap = studentProfile?.skills || {};
    const totalAccumulatedScore = Object.values(studentSkillsMap).reduce((sum, current) => sum + current, 0);

    return Math.round((totalAccumulatedScore / (totalSkillsInCurriculum * 5)) * 100) || 0;
  };

  const progressPercent = calculateStudentProgress();

  const getTrafficLightColor = (percent) => {
    if (percent >= 80) return '#22c55e';
    if (percent >= 50) return '#eab308';
    return '#ef4444';
  };

  // ============================================================================
  // SECTION 5: BOOKING ACTIONS
  // ============================================================================
  const handleRecurringSubmit = (e) => {
    e.preventDefault();
    const startHour = parseInt(recurringStartTime.split(':')[0], 10);
    const endHour = parseInt(recurringEndTime.split(':')[0], 10);

    if (startHour >= endHour) {
      alert("❌ End time must be later than the start time!");
      return;
    }

    const targetDayIndex = parseInt(recurringDay, 10); 
    const totalWeeks = parseInt(recurringWeeks, 10);
    let tempStage = [];
    let today = new Date();
    today.setHours(0, 0, 0, 0); 

    for (let i = 0; i < totalWeeks * 7; i++) {
      let lookDate = new Date();
      lookDate.setDate(today.getDate() + i);
      if (lookDate < today) continue;

      let currentDayIndex = lookDate.getDay() || 7; 
      if (currentDayIndex === targetDayIndex) {
        const isoDateStr = lookDate.toISOString().slice(0, 10);
        const hourStr = `${startHour.toString().padStart(2, '0')}:00`;
        tempStage.push({
          id: Math.random(), 
          selected: true,     
          dateString: isoDateStr,
          formattedDate: lookDate.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }),
          time: hourStr,
          duration: endHour - startHour
        });
      }
    }
    setStagedBookings(tempStage);
    alert(`Generated ${tempStage.length} slots. Review and submit below!`);
  };

  const commitAllStagedBookings = async () => {
    const activeStaged = stagedBookings.filter(b => b.selected);
    if (activeStaged.length === 0) { alert("❌ No slots selected."); return; }
    if (!window.confirm(`Commit ${activeStaged.length} lesson blocks?`)) return;

    setIsBooking(true);
    try {
      await Promise.all(activeStaged.map(bSlot => 
        fetch('/api/bookings', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            instructor_id: parseInt(targetInstructorId, 10),
            learner_id: parseInt(learnerId, 10),
            learner_name: learnerName,
            booking_date: bSlot.dateString,
            booking_time: bSlot.time,
            duration: bSlot.duration 
          })
        })
      ));
      alert(`🎉 Success! Booked ${activeStaged.length} slots.`);
      setStagedBookings([]); 
      fetchScheduleData();
      setMobileTab('menu');
    } catch (err) { alert("❌ Failed to commit bookings."); }
    finally { setIsBooking(false); }
  };

  const handleCancelLesson = async (bookingId, dateStr, timeStr) => {
    if (!window.confirm(`Cancel lesson on ${dateStr} at ${timeStr}?`)) return;
    const response = await fetch(`/api/bookings/${bookingId}`, { method: 'DELETE' });
    if (response.ok) { alert("🗑️ Lesson canceled."); fetchScheduleData(); }
  };

  const getWeekDays = (start) => {
    const days = [];
    const tempDate = new Date(start);
    const day = tempDate.getDay();
    const diff = tempDate.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(tempDate.setDate(diff));
    for (let i = 0; i < 7; i++) { days.push(new Date(monday)); monday.setDate(monday.getDate() + 1); }
    return days;
  };

  const weekDates = getWeekDays(currentWeekStart);
  const myPersonalBookings = existingBookings.filter(b => parseInt(b.learner_id, 10) === parseInt(learnerId, 10));

  if (!instructor) return <div style={{ color: '#fff', padding: '50px', textAlign: 'center', background: '#0f172a', minHeight: '100vh', fontWeight: 'bold' }}>Loading Portal...</div>;

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', color: '#ffffff', fontFamily: 'inherit', paddingBottom: '50px', boxSizing: 'border-box' }}>
      
      {/* --- MINIMALIST APP HEADER --- */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', background: '#090d16', borderBottom: '1px solid rgba(255,255,255,0.06)', position: 'sticky', top: 0, zIndex: 1000 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <img src={schoolLogo} alt="Logo" style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #3b82f6' }} />
          <div>
            <div style={{ color: '#fff', fontWeight: '800', fontSize: '1.05rem', lineHeight: '1.2' }}>LessonLane</div>
            <div style={{ color: '#94a3b8', fontSize: '0.75rem', lineHeight: '1.2' }}>Portal: {learnerName}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button onClick={() => setShowSettings(true)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', fontSize: '0.75rem', padding: '6px 10px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>⚙️ Settings</button>
          <button onClick={() => { localStorage.clear(); navigate('/'); }} style={{ background: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.4)', color: '#fca5a5', fontSize: '0.75rem', padding: '6px 10px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>Logout</button>
        </div>
      </div>

      {/* --- BACK BUTTON (Shown when inside a specific tab) --- */}
      {mobileTab !== 'menu' && (
        <div style={{ padding: '14px 16px 0 16px' }}>
          <button 
            onClick={() => setMobileTab('menu')}
            style={{
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.15)',
              color: '#ffffff',
              padding: '8px 14px',
              borderRadius: '8px',
              fontWeight: '700',
              fontSize: '0.8rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            ← Back to Menu
          </button>
        </div>
      )}

      {/* --- MAIN MOBILE CONTENT AREA --- */}
      <div style={{ padding: '16px', maxWidth: '500px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        
        {/* Welcome Greeting Banner (Shown only on menu view) */}
        {mobileTab === 'menu' && (
          <div style={{ background: '#162238', padding: '16px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 4px 15px rgba(0,0,0,0.2)' }}>
            <h4 style={{ margin: '0 0 4px 0', color: '#ffffff', fontSize: '1rem', fontWeight: '800' }}>Welcome back, {learnerName} 👋</h4>
            <p style={{ margin: 0, fontSize: '0.8rem', color: '#94a3b8', lineHeight: '1.4' }}>Manage your lesson schedules, bulk packages, and progress below.</p>
          </div>
        )}

        {/* --- SHARP FRONT-PAGE GRADIENT TILES (Shown only on menu view) --- */}
        {mobileTab === 'menu' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            
            {/* Tile 1: Weekly Calendar */}
            <div 
              onClick={() => setMobileTab('book')}
              style={{ 
                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)', 
                padding: '18px 14px', 
                borderRadius: '16px', 
                textAlign: 'center', 
                boxShadow: '0 8px 20px rgba(37, 99, 235, 0.3)', 
                border: '1px solid rgba(255,255,255,0.2)', 
                cursor: 'pointer', 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'space-between'
              }}
            >
              <div style={{ width: '36px', height: '36px', background: 'rgba(255,255,255,0.2)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px', fontSize: '18px' }}>
                📅
              </div>
              <div>
                <div style={{ fontWeight: '800', color: '#ffffff', fontSize: '0.95rem', letterSpacing: '-0.3px', lineHeight: '1.2' }}>Weekly Calendar</div>
                <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.85)', marginTop: '3px' }}>Book & manage slots</div>
              </div>
            </div>

            {/* Tile 2: Month Bulk Plan */}
            <div 
              onClick={() => setMobileTab('bulk')}
              style={{ 
                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)', 
                padding: '18px 14px', 
                borderRadius: '16px', 
                textAlign: 'center', 
                boxShadow: '0 8px 20px rgba(5, 150, 105, 0.3)', 
                border: '1px solid rgba(255,255,255,0.2)', 
                cursor: 'pointer', 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'space-between'
              }}
            >
              <div style={{ width: '36px', height: '36px', background: 'rgba(255,255,255,0.2)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px', fontSize: '18px' }}>
                🔄
              </div>
              <div>
                <div style={{ fontWeight: '800', color: '#ffffff', fontSize: '0.95rem', letterSpacing: '-0.3px', lineHeight: '1.2' }}>Month Bulk Plan</div>
                <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.85)', marginTop: '3px' }}>Recurring packages</div>
              </div>
            </div>

            {/* Tile 3: Progress Hub */}
            <div 
              onClick={() => setMobileTab('progress')}
              style={{ 
                background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)', 
                padding: '18px 14px', 
                borderRadius: '16px', 
                textAlign: 'center', 
                boxShadow: '0 8px 20px rgba(124, 58, 237, 0.3)', 
                border: '1px solid rgba(255,255,255,0.2)', 
                cursor: 'pointer', 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'space-between'
              }}
            >
              <div style={{ width: '36px', height: '36px', background: 'rgba(255,255,255,0.2)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px', fontSize: '18px' }}>
                📊
              </div>
              <div>
                <div style={{ fontWeight: '800', color: '#ffffff', fontSize: '0.95rem', letterSpacing: '-0.3px', lineHeight: '1.2' }}>Progress Hub</div>
                <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.85)', marginTop: '3px' }}>Competency matrix</div>
              </div>
            </div>

            {/* Tile 4: Direct Chat */}
            <div 
              onClick={() => setMobileTab('chat')}
              style={{ 
                background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)', 
                padding: '18px 14px', 
                borderRadius: '16px', 
                textAlign: 'center', 
                boxShadow: '0 8px 20px rgba(217, 119, 6, 0.3)', 
                border: '1px solid rgba(255,255,255,0.2)', 
                cursor: 'pointer', 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'space-between'
              }}
            >
              <div style={{ width: '36px', height: '36px', background: 'rgba(255,255,255,0.2)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px', fontSize: '18px' }}>
                💬
              </div>
              <div>
                <div style={{ fontWeight: '800', color: '#ffffff', fontSize: '0.95rem', letterSpacing: '-0.3px', lineHeight: '1.2' }}>Direct Chat</div>
                <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.85)', marginTop: '3px' }}>Instructor inbox</div>
              </div>
            </div>

          </div>
        )}

        {/* --- ACTIVE TAB CONTENT CONTAINER (Hidden when on menu) --- */}
        {mobileTab !== 'menu' && (
          <div style={{ background: '#162238', padding: '18px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.08)' }}>
            
            {/* TAB 1: BOOKING */}
            {mobileTab === 'book' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#94a3b8' }}>📅 Tap slot to book / cancel</span>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button onClick={() => { const d = new Date(currentWeekStart); d.setDate(d.getDate() - 7); setCurrentWeekStart(new Date(d)); }} style={{ padding: '5px 10px', fontSize: '11px', fontWeight: 'bold', background: '#0f172a', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', borderRadius: '6px' }}>◀ Prev</button>
                    <button onClick={() => setCurrentWeekStart(new Date())} style={{ padding: '5px 10px', fontSize: '11px', fontWeight: 'bold', background: '#0f172a', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', borderRadius: '6px' }}>Today</button>
                    <button onClick={() => { const d = new Date(currentWeekStart); d.setDate(d.getDate() + 7); setCurrentWeekStart(new Date(d)); }} style={{ padding: '5px 10px', fontSize: '11px', fontWeight: 'bold', background: '#0f172a', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', borderRadius: '6px' }}>Next ▶</button>
                  </div>
                </div>

                <div style={{ overflowX: 'auto' }}>
                  <BookingCalendar 
                    instructor={targetInstructorId}
                    template={template}
                    existingBookings={existingBookings}
                    learnerId={learnerId}
                    learnerName={learnerName}
                    role="learner"
                    workStart={instructor?.work_start_time?.substring(0, 5) || "08:00"}
                    workEnd={instructor?.work_end_time?.substring(0, 5) || "18:00"}
                    timeLabels={timeLabels}
                    weekDates={weekDates}
                    onBookingSuccess={fetchScheduleData}
                    onCellClick={(date, time, isStudentReserved, liveBooking) => {
                      const dKey = date.toDateString();
                      const isMyOwn = myPersonalBookings.some(b => {
                        const bDateStr = new Date(b.booking_date).toDateString();
                        return (bDateStr === dKey || b.booking_date === dKey) && b.booking_time.substring(0, 5) === time;
                      });
                      if (isMyOwn && liveBooking) handleCancelLesson(liveBooking.id, dKey, time);
                    }}
                  />
                </div>
              </div>
            )}

            {/* TAB 2: BULK PLAN */}
            {mobileTab === 'bulk' && (
              <div>
                <h3 style={{ marginTop: 0, color: '#60a5fa', fontSize: '1.1rem', fontWeight: '800', marginBottom: '12px' }}>🔄 Setup Bulk Plan</h3>
                <form onSubmit={handleRecurringSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '0.8rem' }}>
                  <label style={{ fontWeight: 'bold', color: '#94a3b8', fontSize: '0.75rem' }}>📅 DAY OF THE WEEK</label>
                  <select value={recurringDay} onChange={e => setRecurringDay(e.target.value)} style={{ padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: '#0f172a', color: '#fff', fontSize: '13px' }}>
                    <option value="1">Every Monday</option>
                    <option value="2">Every Tuesday</option>
                    <option value="3">Every Wednesday</option>
                    <option value="4">Every Thursday</option>
                    <option value="5">Every Friday</option>
                    <option value="6">Every Saturday</option>
                    <option value="7">Every Sunday</option>
                  </select>

                  <div style={{ display: 'flex', gap: '10px' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '4px', color: '#94a3b8', fontSize: '0.75rem' }}>START TIME</label>
                      <select value={recurringStartTime} onChange={e => setRecurringStartTime(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: '#0f172a', color: '#fff', fontSize: '13px' }}>
                        {timeLabels.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '4px', color: '#94a3b8', fontSize: '0.75rem' }}>END TIME</label>
                      <select value={recurringEndTime} onChange={e => setRecurringEndTime(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: '#0f172a', color: '#fff', fontSize: '13px' }}>
                        {timeLabels.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>

                  <label style={{ fontWeight: 'bold', color: '#94a3b8', fontSize: '0.75rem' }}>🔁 REPEAT HORIZON</label>
                  <select value={recurringWeeks} onChange={e => setRecurringWeeks(e.target.value)} style={{ padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: '#0f172a', color: '#fff', fontSize: '13px' }}>
                    <option value="2">2 Weeks Package</option>
                    <option value="4">4 Weeks (1 Month Plan)</option>
                    <option value="8">8 Weeks (2 Month Plan)</option>
                  </select>

                  <button type="submit" style={{ background: '#3b82f6', color: '#fff', border: 'none', padding: '11px', borderRadius: '8px', fontWeight: 'bold', marginTop: '6px', cursor: 'pointer', boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)' }}>Generate Preview 📋</button>
                </form>

                {stagedBookings.length > 0 && (
                  <div style={{ marginTop: '16px', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '14px' }}>
                    <h4 style={{ margin: '0 0 8px 0', color: '#34d399', fontSize: '0.9rem' }}>🛒 Review Cart ({stagedBookings.filter(b => b.selected).length})</h4>
                    <div style={{ maxHeight: '150px', overflowY: 'auto', background: '#0f172a', padding: '10px', borderRadius: '8px', marginBottom: '12px', border: '1px solid rgba(255,255,255,0.08)' }}>
                      {stagedBookings.map(b => (
                        <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', marginBottom: '6px' }}>
                          <input type="checkbox" checked={b.selected} onChange={() => setStagedBookings(prev => prev.map(item => item.id === b.id ? { ...item, selected: !item.selected } : item))} />
                          <span style={{ color: '#e2e8f0' }}>{b.formattedDate} ({b.time})</span>
                        </div>
                      ))}
                    </div>
                    <button onClick={commitAllStagedBookings} disabled={isBooking} style={{ background: '#059669', color: '#fff', border: 'none', padding: '11px', width: '100%', fontWeight: 'bold', borderRadius: '8px', cursor: 'pointer', boxShadow: '0 4px 12px rgba(5, 150, 105, 0.4)' }}>Submit Bookings ✅</button>
                  </div>
                )}
              </div>
            )}

            {/* TAB 3: PROGRESS HUB */}
            {mobileTab === 'progress' && (() => {
              const studentFramework = DYNAMIC_VEHICLE_FRAMEWORKS[studentProfile?.training_category || "Car (Manual)"] || {};
              const loggedHours = studentHistoricalLogs.reduce((sum, log) => sum + (log.duration_minutes || 60), 0) / 60;
              const totalHoursDriven = (Number(studentProfile?.prior_hours_experience || 0) + loggedHours).toFixed(1);
              const selectedLog = studentHistoricalLogs.find(l => String(l.id) === String(selectedAuditLogId)) || studentHistoricalLogs[0];

              const handlePrintStudentReport = () => {
                  let competencyHtml = '';
                  Object.entries(studentFramework).forEach(([category, skills]) => {
                      const totalPossible = skills.length * 5;
                      const totalEarned = skills.reduce((sum, skill) => sum + (studentProfile?.skills?.[skill] || 1), 0);
                      const catPercent = Math.round((totalEarned / totalPossible) * 100);
                      const catBarColor = catPercent >= 80 ? '#22c55e' : catPercent >= 50 ? '#eab308' : '#ef4444';

                      let subSkillsHtml = '';
                      skills.forEach(skill => {
                          const score = studentProfile?.skills?.[skill] || 1;
                          const subPercent = Math.round((score / 5) * 100);
                          const subBarColor = score === 5 ? '#22c55e' : score >= 3 ? '#0066cc' : '#ef4444';

                          subSkillsHtml += `
                              <div style="margin-top: 4px; background: #ffffff; padding: 4px 6px; border: 1px solid #e2e8f0; border-radius: 3px;">
                                  <div style="display: flex; justify-content: space-between; font-size: 9px; font-weight: 600; margin-bottom: 2px; color: #334155;">
                                      <span>${skill}</span>
                                      <span style="color: ${subBarColor};">${score}/5</span>
                                  </div>
                                  <div style="width: 100%; height: 4px; background: #e2e8f0; border-radius: 2px; overflow: hidden;">
                                      <div style="width: ${subPercent}%; height: 100%; background: ${subBarColor};"></div>
                                  </div>
                              </div>
                          `;
                      });

                      competencyHtml += `
                          <div style="background: #f8f9fa; padding: 10px; border: 1px solid #cbd5e0; border-radius: 6px; margin-bottom: 10px;">
                              <div style="display: flex; justify-content: space-between; font-size: 11px; font-weight: bold; margin-bottom: 4px; color: #1e293b;">
                                  <span>${category}</span>
                                  <span style="color: ${catBarColor};">${catPercent}%</span>
                              </div>
                              <div style="width: 100%; height: 6px; background: #e2e8f0; border-radius: 3px; overflow: hidden; margin-bottom: 6px;">
                                  <div style="width: ${catPercent}%; height: 100%; background: ${catBarColor};"></div>
                              </div>
                              ${subSkillsHtml}
                          </div>
                      `;
                  });

                  const printWindow = window.open('', '_blank');
                  printWindow.document.write(`
                      <!DOCTYPE html>
                      <html>
                      <head>
                          <title>Student Progress Summary - ${learnerName}</title>
                          <style>
                              body { font-family: Arial, sans-serif; color: #111; padding: 15px; max-width: 800px; margin: 0 auto; }
                              h1 { color: #0066cc; font-size: 16px; margin: 0; }
                              @media print { button { display: none; } }
                          </style>
                      </head>
                      <body>
                          <div style="display: flex; justify-content: space-between; border-bottom: 2px solid #0066cc; padding-bottom: 8px; margin-bottom: 15px;">
                              <div>
                                  <h1>🚗 LessonLane Training Summary</h1>
                                  <p style="margin: 0; font-size: 11px; color: #64748b;">Official Student Performance Report</p>
                              </div>
                              <div style="text-align: right; font-size: 11px;">
                                <strong>${learnerName}</strong><br/>
                                <span style="color: #64748b;">${new Date().toLocaleDateString('en-GB')}</span>
                              </div>
                          </div>
                          <div>${competencyHtml}</div>
                          <script>window.onload = function() { window.print(); }</script>
                      </body>
                      </html>
                  `);
                  printWindow.document.close();
                  printWindow.focus();
              };

              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  
                  {/* Print Report Button */}
                  <button onClick={handlePrintStudentReport} style={{ background: '#3b82f6', color: '#ffffff', border: 'none', padding: '11px', borderRadius: '8px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer', width: '100%', boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)' }}>
                    🖨️ Print Progress Report
                  </button>

                  <div style={{ background: '#0f172a', padding: '14px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)', fontSize: '0.8rem' }}>
                    <div style={{ fontWeight: 'bold', color: '#60a5fa', marginBottom: '4px' }}>🕒 Total Training Volume</div>
                    <div style={{ color: '#e2e8f0' }}><b>{totalHoursDriven} Hours Total</b> ({loggedHours.toFixed(1)} logged across {studentHistoricalLogs.length} sessions).</div>
                    <div style={{ marginTop: '10px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', marginBottom: '4px', color: '#94a3b8' }}>
                        <span>Syllabus Mastery</span><span style={{ color: '#34d399' }}>{progressPercent}%</span>
                      </div>
                      <div style={{ width: '100%', height: '6px', background: '#162238', borderRadius: '3px', overflow: 'hidden' }}>
                        <div style={{ width: `${progressPercent}%`, height: '100%', background: '#3b82f6' }}></div>
                      </div>
                    </div>
                  </div>

                  <div style={{ background: '#0f172a', padding: '14px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#60a5fa', fontSize: '0.9rem', fontWeight: '800' }}>📁 Core Competency Breakdown</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                      {Object.entries(studentFramework).map(([category, skills]) => {
                        const totalPossible = skills.length * 5;
                        const totalEarned = skills.reduce((sum, skill) => sum + (studentProfile?.skills?.[skill] || 1), 0);
                        const catPercent = Math.round((totalEarned / totalPossible) * 100);
                        const isExpanded = expandedCategory === category;

                        return (
                          <div key={category} onClick={() => setExpandedCategory(isExpanded ? null : category)} style={{ background: '#162238', padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.06)', cursor: 'pointer' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', fontWeight: 'bold', marginBottom: '6px' }}>
                              <span style={{ maxWidth: '70%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#e2e8f0' }}>{category}</span>
                              <span style={{ color: getTrafficLightColor(catPercent) }}>{catPercent}%</span>
                            </div>
                            <div style={{ width: '100%', height: '4px', background: '#0f172a', borderRadius: '2px', overflow: 'hidden' }}>
                              <div style={{ width: `${catPercent}%`, height: '100%', background: getTrafficLightColor(catPercent) }}></div>
                            </div>
                            {isExpanded && (
                              <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', gap: '6px' }} onClick={e => e.stopPropagation()}>
                                {skills.map(skill => {
                                  const score = studentProfile?.skills?.[skill] || 1;
                                  return (
                                    <div key={skill} style={{ fontSize: '0.68rem', display: 'flex', justifyContent: 'space-between', color: '#cbd5e1' }}>
                                      <span style={{ maxWidth: '75%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{skill}</span>
                                      <span style={{ fontWeight: 'bold', color: '#60a5fa' }}>{score}/5</span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div style={{ background: '#0f172a', padding: '14px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <h4 style={{ margin: 0, color: '#60a5fa', fontSize: '0.9rem', fontWeight: '800' }}>📜 Past Lesson Audit</h4>
                      {studentHistoricalLogs.length > 0 && (
                        <select value={selectedAuditLogId} onChange={e => setSelectedAuditLogId(e.target.value)} style={{ padding: '5px', fontSize: '11px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.15)', background: '#162238', color: '#fff' }}>
                          {studentHistoricalLogs.map(log => (
                            <option key={log.id} value={log.id}>📅 {new Date(log.lesson_date).toLocaleDateString('en-GB')}</option>
                          ))}
                        </select>
                      )}
                    </div>
                    {selectedLog ? (
                      <div style={{ background: '#162238', padding: '10px', borderRadius: '8px', fontSize: '0.8rem', border: '1px solid rgba(255,255,255,0.06)' }}>
                        <div style={{ fontWeight: 'bold', color: '#34d399', marginBottom: '4px' }}>Grade: {selectedLog.progress_score}/5</div>
                        <p style={{ margin: '0 0 4px 0', color: '#cbd5e1' }}><b>Notes:</b> {selectedLog.custom_notes || 'No summary notes.'}</p>
                      </div>
                    ) : (
                      <p style={{ fontSize: '0.8rem', color: '#94a3b8', fontStyle: 'italic', margin: 0 }}>No past logs found.</p>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* TAB 4: DIRECT CHAT */}
            {mobileTab === 'chat' && (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <h3 style={{ margin: '0 0 12px 0', color: '#60a5fa', fontSize: '1.1rem', fontWeight: '800' }}>💬 Direct Chat with Instructor</h3>
                <div style={{ height: '350px', overflowY: 'auto', background: '#0f172a', padding: '12px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '12px' }}>
                  {messagesList.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#94a3b8', marginTop: '100px', fontSize: '0.85rem' }}>No messages yet. Start the conversation below!</div>
                  ) : (
                    messagesList.map(msg => {
                      const isMe = msg.sender_role === 'learner';
                      return (
                        <div key={msg.id} style={{ alignSelf: isMe ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
                          <div style={{ background: isMe ? '#3b82f6' : '#1e293b', color: '#ffffff', padding: '10px 14px', borderRadius: '10px', border: isMe ? 'none' : '1px solid rgba(255,255,255,0.1)', fontSize: '0.85rem', boxShadow: '0 2px 5px rgba(0,0,0,0.2)' }}>
                            {msg.message_text}
                          </div>
                          <span style={{ fontSize: '10px', color: '#94a3b8', marginTop: '3px', display: 'block', textAlign: isMe ? 'right' : 'left' }}>
                            {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
                <form onSubmit={handleSendMessage} style={{ display: 'flex', gap: '8px' }}>
                  <input 
                    type="text" 
                    value={newMessageText} 
                    onChange={e => setNewMessageText(e.target.value)} 
                    placeholder="Type message..." 
                    style={{ flex: 1, padding: '11px 13px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', fontSize: '13px', background: '#0f172a', color: '#fff', outline: 'none' }}
                  />
                  <button type="submit" style={{ background: '#3b82f6', color: '#fff', border: 'none', padding: '11px 16px', borderRadius: '8px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer', boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)' }}>Send 🚀</button>
                </form>
              </div>
            )}

          </div>
        )}

      </div>

      {/* --- SETTINGS MODAL --- */}
      {showSettings && (
        <div style={Styles.modalOverlay}>
          <div style={{ ...Styles.modalContent, width: '90%', maxWidth: '400px', maxHeight: '85vh', overflowY: 'auto', background: '#162238', color: '#fff', padding: '20px', borderRadius: '16px', border: '1.5px solid #3b82f6' }}>
            <h3 style={{ color: '#60a5fa', marginTop: 0 }}>Account Settings</h3>
            <StudentOnboarding studentId={learnerId} onComplete={() => { setShowSettings(false); fetchScheduleData(); }} />
            <button onClick={() => setShowSettings(false)} style={{ background: '#334155', color: '#fff', width: '100%', marginTop: '12px', border: 'none', padding: '10px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>Close</button>
          </div>
        </div>
      )}

      <LearnerHelpModal isOpen={showHelpModal} onClose={() => setShowHelpModal(false)} />
    </div>
  );
}

export default LearnerDashMobile;